/*
 * HR > Team: everyone on the team, their role, whether they can sign in, the See job prices tick, the My pay switch
 * (question 50; off hides My pay and the server refuses it), active switch, Change role (crew, dispatcher, manager), and
 * inviting someone new (by email for the office, by phone for crew). Owners and managers also see how each person is paid
 * (by the job at a percent, or by the hour at a rate) and change it.
 */
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  canChangeRoleOf,
  canGiveRole,
  ROLES,
  teamRolesFor,
  TWO_STEP_ROLES,
  workerWord,
  type Role,
  type TeamRole,
} from "@steward/shared";
import { useState } from "react";
import { ApiError } from "../api/client";
import { useInvite, useMyPerson, usePeople, useSetPersonRole, useUpdatePerson, type Person } from "../api/hooks";
import { usePayPeople, useSetPay } from "../api/payroll";
import { moneyExact } from "../components/format";
import { useSession } from "../app/session";
import { DataTable } from "../components/DataTable";
import { Choice, InputText } from "../components/fields";
import { Menu, Modal, useToast } from "../components/overlays";
import { Avatar, Banner, Button, Chip, Page, ROLE_WORDS, StatusLabel, Switch } from "../components/parts";
import { Icon } from "../components/Icon";
import { SettingsButton } from "./settings/Feature";

const PEOPLE_ROLE_WORDS: Record<string, string> = { owner: "Owner", manager: "Manager", dispatch: "Dispatcher", crew: "Crew" };

/** What each role can do, under its name in the invite and Change role pickers. */
const ROLE_HINTS: Record<Role, string> = {
  crew: "Their own day and pay",
  dispatch: "Schedule, clients, sending invoices",
  manager: "Everything but settings and keys",
  owner: "Everything",
  franchise_owner: "",
};

export function Team() {
  const { company, can, role: myRole } = useSession();
  const people = usePeople(company.id);
  const me = useMyPerson(company.id);
  const update = useUpdatePerson();
  const toast = useToast();
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const [inviting, setInviting] = useState(Boolean(search.invite));
  const [show, setShow] = useState<"all" | "active" | "inactive">("active");
  const rows = (people.data ?? []).filter((p) => (show === "all" ? true : show === "active" ? p.active : !p.active));
  const worker = workerWord(company.trade);
  const editable = can("people.update");
  const pay = usePayPeople(company.id, can("payroll.read"));
  const payOf = new Map((pay.data ?? []).map((p) => [p.id, p]));
  const [paying, setPaying] = useState<Person | null>(null);
  const [roling, setRoling] = useState<Person | null>(null);
  const canRole = (p: Person) => can("people.update_roles") && p.id !== me.data?.id && canChangeRoleOf(myRole, p.role);

  const set = (
    p: Person,
    patch: { seesPrices?: boolean; showsMyPay?: boolean; countsOnSchedule?: boolean; active?: boolean },
    done: string,
    undo: { seesPrices?: boolean; showsMyPay?: boolean; countsOnSchedule?: boolean; active?: boolean },
  ) => {
    update.mutate(
      { id: p.id, patch },
      {
        onSuccess: () => toast(done, { undo: () => update.mutate({ id: p.id, patch: undo }) }),
        onError: (e) => toast(e instanceof ApiError ? e.message : "That didn't save. Try again.", { kind: "error" }),
      },
    );
  };

  const all = people.data ?? [];
  const counts = { all: all.length, active: all.filter((p) => p.active).length, inactive: all.filter((p) => !p.active).length };

  return (
    <Page
      title="Team"
      subtitle={`People who work at ${company.name}`}
      secondaryAction={<SettingsButton to="/hr/settings">HR settings</SettingsButton>}
      primaryAction={
        can("people.invite") ? (
          <Button icon="plus" onClick={() => setInviting(true)}>
            Invite
          </Button>
        ) : null
      }
    >
      <div className="tiles">
        <Tile label="On the team" value={counts.active} hint={`${counts.inactive} not active`} />
        <Tile label="Can sign in" value={all.filter((p) => p.hasLogin && p.active).length} hint="Have accepted their invite" />
        <Tile
          label={worker.charAt(0).toUpperCase() + worker.slice(1)}
          value={all.filter((p) => p.role === "crew" && p.active).length}
          hint="Sign in with a texted code"
        />
        <Tile
          label="See job prices"
          value={all.filter((p) => p.role === "crew" && p.seesPrices).length}
          hint={`${worker} with the tick on`}
        />
      </div>
      <DataTable
        title="All people"
        rows={rows}
        rowKey={(p) => p.id}
        search={(p) => `${p.firstName} ${p.lastName} ${p.role}`}
        tools={
          <div className="chips">
            <Chip pressed={show === "active"} onClick={() => setShow("active")} count={counts.active}>
              Active
            </Chip>
            <Chip pressed={show === "inactive"} onClick={() => setShow("inactive")} count={counts.inactive}>
              Not active
            </Chip>
            <Chip pressed={show === "all"} onClick={() => setShow("all")} count={counts.all}>
              All
            </Chip>
          </div>
        }
        empty={show === "inactive" ? "Nobody is set to not active." : "Invite your first person to the team."}
        columns={[
          {
            key: "name",
            header: "Name",
            sort: (p) => `${p.firstName} ${p.lastName}`,
            render: (p) => (
              <div className="person">
                <Avatar name={`${p.firstName} ${p.lastName}`} person={p} />
                <div className="name">
                  <b>{p.name}</b>
                  <span>
                    {p.firstName} {p.lastName}
                  </span>
                </div>
              </div>
            ),
          },
          { key: "role", header: "Role", sort: (p) => p.role, render: (p) => PEOPLE_ROLE_WORDS[p.role] ?? p.role },
          ...(can("payroll.read")
            ? [
                {
                  key: "pay",
                  header: "Pay",
                  render: (p: Person) => {
                    const x = payOf.get(p.id);
                    if (!x) return <span className="muted">Loading</span>;
                    if (x.payType === "hourly")
                      return x.hourlyRateCents ? (
                        `${moneyExact(x.hourlyRateCents)} an hour`
                      ) : (
                        <StatusLabel status="warning">No rate set</StatusLabel>
                      );
                    return x.percent ? (
                      `${String(x.percent)}% of each job`
                    ) : (
                      <StatusLabel status="warning">No percent set</StatusLabel>
                    );
                  },
                },
              ]
            : []),
          {
            key: "login",
            header: "Sign in",
            render: (p) =>
              p.hasLogin ? (
                <StatusLabel status="success">Can sign in</StatusLabel>
              ) : (
                <StatusLabel status="informative">Invited</StatusLabel>
              ),
          },
          {
            key: "prices",
            header: "See job prices",
            render: (p) =>
              p.role === "crew" ? (
                <span onClick={(e) => e.stopPropagation()}>
                  <Switch
                    label={`${p.name} sees job prices`}
                    checked={p.seesPrices}
                    disabled={!editable}
                    onChange={(v) =>
                      set(p, { seesPrices: v }, v ? `${p.name} now sees job prices` : `${p.name} no longer sees job prices`, {
                        seesPrices: !v,
                      })
                    }
                  />
                </span>
              ) : (
                <span className="muted">Always</span>
              ),
          },
          {
            key: "day",
            header: "Their day",
            render: (p) =>
              p.role === "crew" && can("security.read") ? (
                <span onClick={(e) => e.stopPropagation()}>
                  <Link to="/hr/team/$personId/day" params={{ personId: p.id }}>
                    See their day
                  </Link>
                </span>
              ) : null,
          },
          {
            key: "counts",
            header: "Counts on schedule",
            render: (p) => (
              <span onClick={(e) => e.stopPropagation()}>
                <Switch
                  label={`${p.name} counts on the schedule`}
                  checked={p.countsOnSchedule}
                  disabled={!editable}
                  onChange={(v) =>
                    set(
                      p,
                      { countsOnSchedule: v },
                      v ? `${p.name} now counts on the schedule` : `${p.name} no longer counts on the schedule`,
                      { countsOnSchedule: !v },
                    )
                  }
                />
              </span>
            ),
          },
          {
            key: "mypay",
            header: "My pay",
            render: (p) => (
              <span onClick={(e) => e.stopPropagation()}>
                <Switch
                  label={`${p.name} sees My pay`}
                  checked={p.showsMyPay}
                  disabled={!editable}
                  onChange={(v) =>
                    set(p, { showsMyPay: v }, v ? `${p.name} now sees My pay` : `My pay is hidden for ${p.name}`, {
                      showsMyPay: !v,
                    })
                  }
                />
              </span>
            ),
          },
          {
            key: "status",
            header: "Status",
            sort: (p) => (p.active ? 0 : 1),
            render: (p) =>
              p.active ? (
                <StatusLabel status="success">Active</StatusLabel>
              ) : (
                <StatusLabel status="inactive">Not active</StatusLabel>
              ),
          },
          {
            key: "more",
            header: "",
            align: "right",
            render: (p) =>
              editable ? (
                <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-block" }}>
                  <Menu
                    button={({ toggle, open }) => (
                      <button
                        type="button"
                        className="iconbtn"
                        aria-label={`More for ${p.name}`}
                        aria-expanded={open}
                        onClick={toggle}
                      >
                        <Icon name="more" />
                      </button>
                    )}
                  >
                    {(close) => (
                      <>
                        {can("payroll.update") ? (
                          <button
                            type="button"
                            onClick={() => {
                              close();
                              setPaying(p);
                            }}
                          >
                            Change pay
                          </button>
                        ) : null}
                        {canRole(p) ? (
                          <button
                            type="button"
                            onClick={() => {
                              close();
                              setRoling(p);
                            }}
                          >
                            Change role
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => {
                            close();
                            set(
                              p,
                              { active: !p.active },
                              p.active ? `${p.name} set to not active` : `${p.name} is active again`,
                              {
                                active: p.active,
                              },
                            );
                          }}
                        >
                          {p.active ? "Make not active" : "Make active again"}
                        </button>
                      </>
                    )}
                  </Menu>
                </span>
              ) : null,
          },
        ]}
      />
      {editable ? (
        <p className="muted">
          Making someone not active stops their access to {company.name} right away. Their past work and pay stay.
        </p>
      ) : null}
      {paying ? <PayModal person={paying} onClose={() => setPaying(null)} /> : null}
      {roling ? <RoleModal person={roling} onClose={() => setRoling(null)} /> : null}
      <InviteModal
        open={inviting}
        onClose={() => {
          setInviting(false);
          if (search.invite) void navigate({ to: "/hr/team", search: {} });
        }}
      />
    </Page>
  );
}

function Tile({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div className="tile">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      <span className="hint">{hint}</span>
    </div>
  );
}

const PHONE_ROLES: Role[] = ["crew", "dispatch"];

function InviteModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { company, role: myRole } = useSession();
  const invite = useInvite();
  const toast = useToast();
  const [role, setRole] = useState<Role>("crew");
  const [by, setBy] = useState<"phone" | "email">("phone");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [contact, setContact] = useState("");
  const [locale, setLocale] = useState<"en" | "es">("en");
  const [error, setError] = useState<string | null>(null);
  const worker = workerWord(company.trade);

  const roles = ROLES.filter((r) => canGiveRole(myRole, r) && r !== "franchise_owner");
  const phoneOk = PHONE_ROLES.includes(role);
  const channel = phoneOk ? by : "email";

  const reset = () => {
    setFirst("");
    setLast("");
    setContact("");
    setError(null);
  };
  const send = () => {
    setError(null);
    invite.mutate(
      { role, firstName: first, lastName: last, locale, ...(channel === "email" ? { email: contact } : { phone: contact }) },
      {
        onSuccess: () => {
          toast(`Invite sent to ${first}`);
          reset();
          onClose();
        },
        onError: (e) => setError(e instanceof ApiError ? e.message : "That didn't send. Try again."),
      },
    );
  };

  return (
    <Modal
      title="Invite to the team"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={invite.isPending} onClick={send} disabled={!first.trim() || !contact.trim()}>
            Send invite
          </Button>
        </>
      }
    >
      {error ? <Banner type="error">{error}</Banner> : null}
      <Choice
        label="Role"
        value={role}
        onChange={(r) => setRole(r)}
        options={roles.map((r) => ({
          value: r,
          label: r === "crew" ? worker.charAt(0).toUpperCase() + worker.slice(1) : (ROLE_WORDS[r] ?? r),
          hint: ROLE_HINTS[r],
        }))}
      />
      <div className="form-grid">
        <InputText label="First name" required value={first} onChange={(e) => setFirst(e.target.value)} />
        <InputText label="Last name" value={last} onChange={(e) => setLast(e.target.value)} />
      </div>
      {phoneOk ? (
        <Choice
          label="Send the invite by"
          value={by}
          onChange={setBy}
          options={[
            { value: "phone", label: "Text", hint: "They sign in with a texted code" },
            { value: "email", label: "Email", hint: "They pick a password" },
          ]}
        />
      ) : (
        <Banner>Owners and managers are invited by email, since they sign in with a password and two-step.</Banner>
      )}
      <InputText
        label={channel === "email" ? "Email" : "Mobile number"}
        type={channel === "email" ? "email" : "tel"}
        inputMode={channel === "email" ? "email" : "tel"}
        required
        value={contact}
        onChange={(e) => setContact(e.target.value)}
        description={channel === "email" ? "The link works for 7 days." : "We text a link that works for 7 days."}
      />
      <Choice
        label="Their language"
        value={locale}
        onChange={setLocale}
        options={[
          { value: "en", label: "English" },
          { value: "es", label: "Spanish", hint: "Texts and their screens in Spanish" },
        ]}
      />
    </Modal>
  );
}

function RoleModal({ person, onClose }: { person: Person; onClose: () => void }) {
  const { company, role: myRole } = useSession();
  const save = useSetPersonRole();
  const toast = useToast();
  const worker = workerWord(company.trade);
  const roles = teamRolesFor(myRole);
  const [role, setRole] = useState<TeamRole>(roles.find((r) => r === person.role) ?? "crew");
  const [error, setError] = useState<string | null>(null);
  const word = (r: TeamRole) => (r === "crew" ? worker.charAt(0).toUpperCase() + worker.slice(1) : (ROLE_WORDS[r] ?? r));
  const go = () => {
    if (role === person.role) {
      onClose();
      return;
    }
    save.mutate(
      { id: person.id, role },
      {
        onSuccess: () => {
          toast(`${person.name} is now ${word(role).toLowerCase()}`);
          onClose();
        },
        onError: (e) => setError(e instanceof ApiError ? e.message : "That didn't save. Try again."),
      },
    );
  };
  return (
    <Modal
      title={`Role for ${person.name}`}
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={go}>
            Save role
          </Button>
        </>
      }
    >
      <div className="stack">
        <Choice
          label="Role"
          value={role}
          onChange={setRole}
          options={roles.map((r) => ({ value: r, label: word(r), hint: ROLE_HINTS[r] }))}
        />
        {TWO_STEP_ROLES.includes(role) && role !== person.role ? (
          <p className="muted">
            {person.name} sets up two-step sign in with an authenticator app the next time they use the Steward.
          </p>
        ) : null}
        <p className="muted">The new role works right away. Their pay, past work and See job prices switch stay.</p>
        {error ? <Banner type="error">{error}</Banner> : null}
      </div>
    </Modal>
  );
}

function PayModal({ person, onClose }: { person: Person; onClose: () => void }) {
  const { company } = useSession();
  const pay = usePayPeople(company.id);
  const now = pay.data?.find((p) => p.id === person.id);
  const save = useSetPay();
  const toast = useToast();
  const [payType, setType] = useState<"percent" | "hourly">(now?.payType ?? "percent");
  const [percent, setPercent] = useState(now?.percent == null ? "" : String(now.percent));
  const [rate, setRate] = useState(now?.hourlyRateCents ? (now.hourlyRateCents / 100).toFixed(2) : "");
  const [error, setError] = useState<string | null>(null);
  const go = () => {
    const pct = Number(percent);
    const cents = Math.round(Number(rate) * 100);
    if (payType === "percent" && !(pct > 0 && pct <= 100)) {
      setError("Type their percent of each job, from 1 to 100.");
      return;
    }
    if (payType === "hourly" && !(cents > 0)) {
      setError("Type their pay for each hour.");
      return;
    }
    save.mutate(
      { id: person.id, patch: payType === "percent" ? { payType, percent: pct } : { payType, hourlyRateCents: cents } },
      {
        onSuccess: () => {
          toast(`${person.name}'s pay saved`);
          onClose();
        },
        onError: (e) => setError(e instanceof ApiError ? e.message : "That didn't save. Try again."),
      },
    );
  };
  return (
    <Modal
      title={`Pay for ${person.name}`}
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={go}>
            Save pay
          </Button>
        </>
      }
    >
      <div className="stack">
        <Choice
          label="How they are paid"
          value={payType}
          onChange={setType}
          options={[
            { value: "percent", label: "By the job", hint: "A percent of each job's price, before tips" },
            { value: "hourly", label: "By the hour", hint: "Their clocked hours times a rate" },
          ]}
        />
        {payType === "percent" ? (
          <InputText
            label="Percent of each job"
            inputMode="decimal"
            suffix="%"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            description="When two people work a job, the highest percent is the pool, shared by their percents."
          />
        ) : (
          <InputText
            label="Pay for each hour"
            inputMode="decimal"
            prefix="$"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            description="Tips still split evenly with everyone on the job."
          />
        )}
        <p className="muted">The new pay counts from the next week that is not paid. Paid weeks keep the pay they had.</p>
        {error ? <Banner type="error">{error}</Banner> : null}
      </div>
    </Modal>
  );
}
